import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  format,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  parseISO,
} from "date-fns";
import { AlertTriangle, Check, Eye, FileCode, FileDown, Info, Loader2, SlidersHorizontal, X, XCircle } from "lucide-react";
import toast from "react-hot-toast";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { DatePickerField } from "@/components/shared/DatePickerField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { cn } from "@/lib/utils";
import { formatDisplayDate } from "@/lib/dateFormat";
import { invoiceKeys } from "@/lib/queryKeys";
import { RecibidaMobileCard } from "@/pages/invoices/RecibidaMobileCard";
import {
  formatRecibidaTotal,
  formatRecibidaTypeSeries,
  getRecibidaIvaAmount,
  getRecibidaNetAmount,
  getRecibidaTotalAmount,
  recibidasStatusIcon,
  recibidasStatusVariant,
} from "@/pages/invoices/recibidas-list-shared";
import {
  acceptRecibida,
  exportRecibidasCsv,
  exportRecibidasXmlZip,
  getRecibidasSummary,
  listRecibidas,
  rejectRecibida,
  downloadRecibidaPdf,
  downloadRecibidaXml,
  type RecibidaItem,
} from "@/services/invoiceService";
import {
  RecibidaRejectDialog,
  type RejectMotivo,
} from "@/pages/invoices/RecibidaRejectDialog";
import { AutoAcceptEmisorPromptDialog } from "@/pages/invoices/AutoAcceptEmisorPromptDialog";
import { RecibidasSummaryCard, RecibidasSummaryCardSkeleton } from "@/pages/invoices/RecibidasSummaryCard";

const formatTotal = formatRecibidaTotal;

const ISO_DATE = "yyyy-MM-dd";

const VALID_PERIODS = ["24h", "7d", "month", "last_month", "custom"] as const;

function getCurrentMonthRange(): { dateFrom: string; dateTo: string } {
  const now = new Date();
  return {
    dateFrom: format(startOfMonth(now), ISO_DATE),
    dateTo: format(endOfMonth(now), ISO_DATE),
  };
}

function periodToDateRange(
  period: (typeof VALID_PERIODS)[number]
): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const today = format(now, ISO_DATE);
  if (period === "24h") {
    const from = format(subDays(now, 1), ISO_DATE);
    return { dateFrom: from, dateTo: today };
  }
  if (period === "7d") {
    const from = format(subDays(now, 6), ISO_DATE);
    return { dateFrom: from, dateTo: today };
  }
  if (period === "month") {
    return {
      dateFrom: format(startOfMonth(now), ISO_DATE),
      dateTo: format(endOfMonth(now), ISO_DATE),
    };
  }
  if (period === "last_month") {
    const lastMonth = subMonths(now, 1);
    return {
      dateFrom: format(startOfMonth(lastMonth), ISO_DATE),
      dateTo: format(endOfMonth(lastMonth), ISO_DATE),
    };
  }
  return getCurrentMonthRange();
}

export function RecibidasPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl({ pageSizeOptions: [10, 20, 50] });

  const periodMonthLabels = useMemo(() => {
    const locale = i18n.language ?? "es";
    const now = new Date();
    const thisMonthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(now);
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(lastMonthDate);
    return { thisMonthName, lastMonthName };
  }, [i18n.language]);
  const defaultRange = getCurrentMonthRange();

  const rawPeriod = searchParams.get("period") ?? undefined;
  const period =
    !rawPeriod ||
    !VALID_PERIODS.includes(rawPeriod as (typeof VALID_PERIODS)[number])
      ? "month"
      : rawPeriod;
  const dateFrom = searchParams.get("dateFrom") ?? defaultRange.dateFrom;
  const dateTo = searchParams.get("dateTo") ?? defaultRange.dateTo;
  const currency = searchParams.get("currency") ?? undefined;
  // When set, the page ignores period/date filters and shows only
  // EnvioCFE-origin rows still pending the receptor's accept/reject
  // decision (DGI §XVIII). Drives the dashboard banner deep-link.
  const pendingAck = searchParams.get("pendingAck") === "1";

  const effectiveRange = useMemo(() => {
    if (period === "custom") {
      return { dateFrom: dateFrom ?? defaultRange.dateFrom, dateTo: dateTo ?? defaultRange.dateTo };
    }
    return periodToDateRange(period as (typeof VALID_PERIODS)[number]);
  }, [period, dateFrom, dateTo, defaultRange.dateFrom, defaultRange.dateTo]);

  const setFilter = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("period", value);
          next.delete("dateFrom");
          next.delete("dateTo");
          if (value === "custom") {
            const { dateFrom: from, dateTo: to } = getCurrentMonthRange();
            next.set("dateFrom", from);
            next.set("dateTo", to);
          }
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setCustomDateRange = useCallback(
    (from: string, to: string) => {
      const fromDate = parseISO(from);
      const toDate = parseISO(to);
      if (fromDate > toDate) {
        toast.error(
          t("recibidas.dateFromBeforeDateTo", "From date must be on or before to date.")
        );
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("dateFrom", from);
          next.set("dateTo", to);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, t]
  );

  useEffect(() => {
    if (period === "custom" && (!dateFrom || !dateTo)) {
      const { dateFrom: from, dateTo: to } = getCurrentMonthRange();
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("dateFrom", from);
          next.set("dateTo", to);
          return next;
        },
        { replace: true }
      );
    }
  }, [period, dateFrom, dateTo, setSearchParams]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const hasActiveFilters = !!currency || pendingAck;
  const activeFilterCount = (currency ? 1 : 0) + (pendingAck ? 1 : 0);

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("currency");
        next.delete("pendingAck");
        next.set("page", "1");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const setPendingAck = useCallback(
    (value: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("pendingAck", "1");
          else next.delete("pendingAck");
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearPendingAck = useCallback(() => {
    setPendingAck(false);
  }, [setPendingAck]);

  const setCurrency = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value && value !== "all") next.set("currency", value);
          else next.delete("currency");
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      dateFrom: effectiveRange.dateFrom,
      dateTo: effectiveRange.dateTo,
      page: pagination.page,
      limit: pagination.limit,
      ...(currency ? { currency } : {}),
      ...(pendingAck ? { pendingAck: true } : {}),
    }),
    [
      effectiveRange.dateFrom,
      effectiveRange.dateTo,
      pagination.page,
      pagination.limit,
      currency,
      pendingAck,
    ]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [...invoiceKeys.recibidas(), listParams],
    queryFn: () => listRecibidas(listParams),
    placeholderData: keepPreviousData,
  });

  const summaryParams = useMemo(
    () => ({
      dateFrom: effectiveRange.dateFrom,
      dateTo: effectiveRange.dateTo,
      ...(currency ? { currency } : {}),
      ...(pendingAck ? { pendingAck: true } : {}),
    }),
    [effectiveRange.dateFrom, effectiveRange.dateTo, currency, pendingAck],
  );

  const {
    data: summary,
    isLoading: summaryLoading,
    isPlaceholderData: summaryIsStale,
  } = useQuery({
    queryKey: [...invoiceKeys.recibidas(), "summary", summaryParams],
    queryFn: () => getRecibidasSummary(summaryParams),
    placeholderData: keepPreviousData,
  });

  const summaryPeriodLabel = useMemo(() => {
    if (pendingAck) {
      return t(
        "recibidas.summaryCard.periodPending",
        "Sin importar período",
      );
    }
    if (period === "month") {
      const name = periodMonthLabels.thisMonthName;
      return t("recibidas.summaryCard.periodMonth", {
        month: name.charAt(0).toUpperCase() + name.slice(1),
        defaultValue: "{{month}} — mes en curso",
      });
    }
    if (period === "last_month") {
      const name = periodMonthLabels.lastMonthName;
      return t("recibidas.summaryCard.periodLastMonth", {
        month: name.charAt(0).toUpperCase() + name.slice(1),
        defaultValue: "{{month}} — mes anterior",
      });
    }
    if (period === "24h") {
      return t("recibidas.summaryCard.period24h", "Últimas 24 horas");
    }
    if (period === "7d") {
      return t("recibidas.summaryCard.period7d", "Últimos 7 días");
    }
    if (period === "custom") {
      return t("recibidas.summaryCard.periodCustom", {
        from: effectiveRange.dateFrom,
        to: effectiveRange.dateTo,
        defaultValue: "{{from}} → {{to}}",
      });
    }
    return t("recibidas.summaryCard.periodAll", "Todos los períodos");
  }, [
    pendingAck,
    period,
    periodMonthLabels,
    effectiveRange.dateFrom,
    effectiveRange.dateTo,
    t,
  ]);

  const [isExporting, setIsExporting] = useState(false);
  const [isExportingXml, setIsExportingXml] = useState(false);

  /**
   * Same filters as the CSV export. Rows with no supplier XML are reported
   * rather than silently missing: they arrived via DGI sync, which reports the
   * comprobante's data but not the signed file, so there is nothing to ship.
   */
  const handleExportXml = useCallback(async () => {
    setIsExportingXml(true);
    try {
      const { exported, skipped } = await exportRecibidasXmlZip(summaryParams);
      if (exported === 0) {
        toast.error(
          t(
            "invoices.recibidas.exportXmlNone",
            "No hay XML disponibles para los comprobantes filtrados."
          )
        );
      } else if (skipped > 0) {
        toast.success(
          t("invoices.recibidas.exportXmlPartial", {
            exported,
            skipped,
            defaultValue:
              "{{exported}} XML exportados. {{skipped}} sin XML del proveedor.",
          })
        );
      } else {
        toast.success(
          t("invoices.recibidas.exportXmlOk", {
            exported,
            defaultValue: "{{exported}} XML exportados.",
          })
        );
      }
    } catch {
      toast.error(
        t("invoices.list.exportError", "No se pudo exportar. Intentá de nuevo.")
      );
    } finally {
      setIsExportingXml(false);
    }
  }, [summaryParams, t]);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportRecibidasCsv(summaryParams);
    } catch {
      toast.error(
        t("invoices.list.exportError", "No se pudo exportar. Intentá de nuevo.")
      );
    } finally {
      setIsExporting(false);
    }
  }, [summaryParams, t]);

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(
        t(
          "recibidas.error",
          "Unable to load received invoices. The service may not be available."
        )
      );
    }
  }, [isError, t]);

  const items: RecibidaItem[] = data?.data ?? [];
  const loading = isLoading;

  // Reject dialog state: which row the user is rejecting (null = closed).
  // The dialog itself lives at the bottom of the page so opening it from a
  // row action doesn't tear it down when the menu closes.
  const [rejectingRowId, setRejectingRowId] = useState<string | null>(null);
  // Same pattern for the post-accept "always auto-accept from this emisor?"
  // prompt — populated from the API response and consumed by the
  // AutoAcceptEmisorPromptDialog at the bottom of the page.
  const [autoAcceptPrompt, setAutoAcceptPrompt] = useState<
    { rucEmisor: string; legalName: string | null } | null
  >(null);

  const acceptRowMutation = useMutation({
    mutationFn: (id: string) => acceptRecibida(id),
    onSuccess: (res) => {
      toast.success(
        t("recibidas.ackCfeAcceptedToast", "Aceptada — avisando al emisor"),
      );
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.recibidas() });
      if (res.promptAutoAccept) setAutoAcceptPrompt(res.promptAutoAccept);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });
  const rejectRowMutation = useMutation({
    mutationFn: (args: { id: string; motivos: RejectMotivo[] }) =>
      rejectRecibida(args.id, args.motivos),
    onSuccess: () => {
      toast.success(
        t("recibidas.ackCfeRejectedToast", "Rechazada — enviando ACKCFE al emisor"),
      );
      setRejectingRowId(null);
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.recibidas() });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });

  const getRecibidaRowActions = useCallback(
    (row: RecibidaItem): TableAction[] => {
      const actions: TableAction[] = [
        {
          label: t("recibidas.actionDetails"),
          icon: Eye,
          onClick: () => {
            void navigate(`/invoices/recibidas/${row.id}`);
          },
        },
      ];
      // Accept / Reject only when the row still needs a decision and
      // it arrived via EnvioCFE (only those have an emisor to ack).
      const needsDecision =
        row.isEligibleForAckCfe === true && !row.myAckCfeEstado;
      if (needsDecision) {
        actions.push({
          label: t("recibidas.ackCfeAccept", "Aceptar"),
          icon: Check,
          onClick: () => {
            if (acceptRowMutation.isPending || rejectRowMutation.isPending) return;
            acceptRowMutation.mutate(row.id);
          },
        });
        actions.push({
          label: t("recibidas.ackCfeReject", "Rechazar"),
          icon: XCircle,
          onClick: () => {
            if (acceptRowMutation.isPending || rejectRowMutation.isPending) return;
            setRejectingRowId(row.id);
          },
        });
      }
      actions.push({
        label: t("recibidas.actionDownloadPdf"),
        icon: FileDown,
        onClick: () => {
          void downloadRecibidaPdf(row.id).catch(() => {
            toast.error(t("recibidas.downloadPdfError"));
          });
        },
      });
      if (row.hasRawXml) {
        actions.push({
          label: t("recibidas.actionViewXml"),
          icon: FileCode,
          onClick: () => {
            void downloadRecibidaXml(row.id).catch(() => {
              toast.error(t("recibidas.downloadXmlError"));
            });
          },
        });
      }
      return actions;
    },
    [navigate, t, acceptRowMutation, rejectRowMutation]
  );

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span>{t("recibidas.title", "Received invoices")}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={t("recibidas.syncInfoAriaLabel")}
                >
                  <Info className="size-5" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm p-3 text-left" side="bottom" align="start">
                <p className="text-sm text-foreground">{t("recibidas.syncNote")}</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t("recibidas.paymentMethodHint")}
                </p>
              </TooltipContent>
            </Tooltip>
          </span>
        }
        description={t("recibidas.subtitle")}
      />
      {/* "Pendientes de respuesta" filter active — deep-linked from the
          dashboard banner, or set via Filtros. Date range is ignored
          server-side when this is on; show that explicitly so users
          aren't confused by the "Junio" period selector still showing. */}
      {pendingAck ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--fx-r-md)] border border-[#E8C490] bg-[rgba(232,196,144,0.18)] px-3 py-2 text-sm text-[#8C5A1F] dark:bg-[rgba(232,196,144,0.10)] dark:text-[#E8C490]">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {t(
                "recibidas.pendingAckFilterActive",
                "Filtrando: comprobantes pendientes de tu aceptación o rechazo (todas las fechas).",
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={clearPendingAck}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-[rgba(232,196,144,0.30)]"
          >
            <X className="h-3 w-3" />
            {t("recibidas.pendingAckClear", "Quitar filtro")}
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Período — hidden when pendingAck is on because the date
              range is irrelevant in that mode (the amber chip already
              says "todas las fechas"). Showing a grayed-out "Junio"
              read as broken; hiding cleanly signals "this control
              doesn't apply right now". The chip's "Quitar filtro" X
              restores the period selector. */}
          {!pendingAck && (
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-sm text-muted-foreground">
                {t("invoices.list.filterPeriod", "Date range")}
              </span>
              <Select value={period} onValueChange={setFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder={t("invoices.list.filterPeriod", "Período")}>
                    {period === "month"
                      ? periodMonthLabels.thisMonthName.charAt(0).toUpperCase() + periodMonthLabels.thisMonthName.slice(1)
                      : period === "last_month"
                        ? periodMonthLabels.lastMonthName.charAt(0).toUpperCase() + periodMonthLabels.lastMonthName.slice(1)
                        : period === "24h"
                          ? t("invoices.list.period24h", "Últimas 24h")
                          : period === "7d"
                            ? t("invoices.list.period7d", "Últimos 7 días")
                            : period === "custom"
                              ? t("invoices.list.periodCustom", "Personalizado")
                              : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t("invoices.list.period24h", "Last 24 hours")}</SelectItem>
                  <SelectItem value="7d">{t("invoices.list.period7d", "Last 7 days")}</SelectItem>
                  <SelectItem value="month">
                    {t("invoices.list.periodMonthWithName", { month: periodMonthLabels.thisMonthName })}
                  </SelectItem>
                  <SelectItem value="last_month">
                    {t("invoices.list.periodLastMonthWithName", { month: periodMonthLabels.lastMonthName })}
                  </SelectItem>
                  <SelectItem value="custom">{t("invoices.list.periodCustom", "Custom")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {!pendingAck && period === "custom" && (
            <div className="flex flex-wrap items-end gap-4 pl-1">
              <DatePickerField
                value={dateFrom ?? defaultRange.dateFrom}
                onChange={(v) =>
                  setCustomDateRange(v, dateTo ?? defaultRange.dateTo)
                }
                label={t("invoices.list.filterDateFrom", "From")}
                labelPosition="inline"
                triggerClassName="pr-3"
                className="min-w-0"
                id="recibidas-dateFrom"
              />
              <DatePickerField
                value={dateTo ?? defaultRange.dateTo}
                onChange={(v) =>
                  setCustomDateRange(dateFrom ?? defaultRange.dateFrom, v)
                }
                label={t("invoices.list.filterDateTo", "To")}
                labelPosition="inline"
                triggerClassName="pr-3"
                className="min-w-0"
                id="recibidas-dateTo"
              />
            </div>
          )}

          {/* Other filters — in dialog */}
          <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 gap-2 transition-colors duration-150"
              >
                <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">{t("invoices.list.filters", "Filters")}</span>
                {activeFilterCount > 0 ? (
                  <Badge
                    variant="secondary"
                    className="shrink-0 tabular-nums"
                    aria-label={t("invoices.list.filtersActiveShort", {
                      count: activeFilterCount,
                    })}
                  >
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-xl">
              <DialogHeader className="border-b border-border px-4 py-4 text-left">
                <DialogTitle className="text-foreground">
                  {t("recibidas.filtersTitle", "Filter received invoices")}
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[min(70vh,560px)] overflow-y-auto overscroll-contain px-4 py-4">
                <div className="flex flex-col gap-5">
                  <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                    <span className="shrink-0 text-sm text-muted-foreground">
                      {t("invoices.list.filterCurrency", "Currency")}
                    </span>
                    <Select
                      value={currency ?? "all"}
                      onValueChange={(v) => setCurrency(v === "all" ? "" : v)}
                    >
                      <SelectTrigger className="w-full sm:w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("invoices.list.currencyAll", "All")}</SelectItem>
                        <SelectItem value="UYU">UYU</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Pendientes de respuesta — same flag the dashboard
                      banner sets via deep-link, also available here so
                      the user can opt into the view without leaving
                      the page. Bypasses the date range server-side. */}
                  <label className="flex w-full cursor-pointer items-start gap-3 rounded-md border border-[var(--fx-ink-100)] px-3 py-2.5 transition-colors hover:bg-[var(--fx-s-raised-hover)] dark:border-[var(--dk-border)]">
                    <Checkbox
                      checked={pendingAck}
                      onCheckedChange={(checked) => setPendingAck(checked === true)}
                      className="mt-0.5 shrink-0"
                    />
                    <div className="flex-1 space-y-0.5">
                      <div className="text-sm font-medium">
                        {t(
                          "recibidas.filterPendingAck",
                          "Mostrar solo pendientes de respuesta",
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(
                          "recibidas.filterPendingAckHint",
                          "Comprobantes que todavía no aceptaste o rechazaste. Ignora el filtro de fechas.",
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div
                className={cn(
                  "flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center",
                  hasActiveFilters ? "sm:justify-between" : "sm:justify-end"
                )}
              >
                {hasActiveFilters ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full transition-colors duration-150 sm:w-auto"
                    onClick={() => {
                      clearFilters();
                    }}
                  >
                    {t("invoices.list.clearFilters", "Clear filters")}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  className="w-full transition-colors duration-150 sm:w-auto"
                  onClick={() => setFiltersOpen(false)}
                >
                  {t("invoices.list.filtersDone", "Done")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {hasActiveFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full basis-full shrink-0 text-muted-foreground transition-colors duration-150 sm:w-auto sm:basis-auto"
              onClick={clearFilters}
            >
              {t("invoices.list.clearFilters", "Clear filters")}
            </Button>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={isExporting || (data?.total ?? 0) === 0}
          className="transition-colors duration-150"
          onClick={handleExportCsv}
        >
          {isExporting ? (
            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <FileDown className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          )}
          {t("invoices.list.exportCsv", "Exportar CSV")}
        </Button>
        {/* Same filters and the same disabled rule as the CSV button — two
            adjacent exports must describe the same rows. */}
        <Button
          type="button"
          variant="outline"
          disabled={isExportingXml || (data?.total ?? 0) === 0}
          className="transition-colors duration-150"
          onClick={handleExportXml}
        >
          {isExportingXml ? (
            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <FileCode className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          )}
          {t("invoices.list.exportXml", "Exportar XML")}
        </Button>
      </div>
      {summary && !summaryIsStale ? (
        <RecibidasSummaryCard
          summary={summary}
          periodLabel={summaryPeriodLabel}
          pendingAckMode={pendingAck}
        />
      ) : summaryLoading || summaryIsStale ? (
        <RecibidasSummaryCardSkeleton />
      ) : null}
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("recibidas.empty", "No received invoices in this period.")}
            message={t(
              "recibidas.emptyHint",
              "Change the date range or try again later. Received invoices are loaded from the tax authority."
            )}
          />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {items.map((row) => (
                <RecibidaMobileCard
                  key={row.id}
                  row={row}
                  onOpen={(id) => void navigate(`/invoices/recibidas/${id}`)}
                />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("recibidas.date", "Date")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.emitter", "Emitter")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.typeAndSeries", "Type / Series")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.paymentMethod", "Payment method")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.number", "Number")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.status", "Status")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.total", "Total")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.neto", "Net")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.iva", "VAT")}
                  </TableHead>
                  <TableHead>
                    {t("recibidas.retenido", "Withheld")}
                  </TableHead>
                  <TableHead className="w-[52px] text-right">
                    <span className="sr-only">{t("recibidas.actionsColumn", "Actions")}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => void navigate(`/invoices/recibidas/${row.id}`)}
                  >
                    <TableCell>
                      {row.fechaHora
                        ? formatDisplayDate(row.fechaHora.slice(0, 10), {
                            locale: i18n.language,
                          })
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {row.legalName?.trim() || row.rucEmisor?.trim() || "—"}
                        </span>
                        {row.legalName?.trim() && row.rucEmisor?.trim() && (
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {row.rucEmisor.trim()}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatRecibidaTypeSeries(row, t)}</TableCell>
                    <TableCell
                      className="text-muted-foreground"
                      title={
                        row.formaPago == null
                          ? t("recibidas.paymentMethodDashTitle")
                          : undefined
                      }
                    >
                      {row.formaPago === "credito"
                        ? t("invoices.list.paymentMethodCredito", "Credit")
                        : row.formaPago === "contado"
                          ? t("invoices.list.paymentMethodContado", "Spot")
                          : "—"}
                    </TableCell>
                    <TableCell>{row.nro}</TableCell>
                    <TableCell>
                      {row.estado ? (
                        <StatusBadge
                          variant={recibidasStatusVariant(row.estado)}
                          className="inline-flex max-w-full"
                        >
                          {(() => {
                            const Icon = recibidasStatusIcon(row.estado);
                            const label =
                              row.estado === "AE"
                                ? t("recibidas.statusAE", "Document received")
                                : row.estado === "BE"
                                  ? t("recibidas.statusBE", "Rejected")
                                  : row.estado;
                            return (
                              <>
                                <Icon
                                  className="h-3 w-3 shrink-0"
                                  aria-hidden
                                />
                                <span className="min-w-0 truncate">
                                  {label}
                                </span>
                              </>
                            );
                          })()}
                        </StatusBadge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTotal(row.moneda || "UYU", getRecibidaTotalAmount(row))}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTotal(
                        row.moneda || "UYU",
                        getRecibidaNetAmount(row)
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatTotal(
                        row.moneda || "UYU",
                        getRecibidaIvaAmount(row)
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {(row.totalRetenido ?? 0) !== 0
                        ? formatTotal(row.moneda || "UYU", row.totalRetenido ?? 0)
                        : "—"}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TableActionsMenu actions={getRecibidaRowActions(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("recibidas.rowsPerPage", "Rows per page")}
              pageOfLabel={t("recibidas.pageOf", "{{from}}-{{to}} of {{total}}", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>

      <RecibidaRejectDialog
        open={rejectingRowId != null}
        onOpenChange={(open) => {
          if (!open) setRejectingRowId(null);
        }}
        onSubmit={(motivos) => {
          if (rejectingRowId == null) return;
          rejectRowMutation.mutate({ id: rejectingRowId, motivos });
        }}
        submitting={rejectRowMutation.isPending}
        t={t}
      />

      <AutoAcceptEmisorPromptDialog
        prompt={autoAcceptPrompt}
        onClose={() => setAutoAcceptPrompt(null)}
      />
    </div>
  );
}
